import React from 'react';
import { navigate } from '@reach/router';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { Link } from 'gatsby';
import * as R from 'ramda';
import { graphql } from 'react-apollo';
import { Flex, Box } from '@cowsquare/design';
import CheckoutForm from '../../../CheckoutForm';
import { GET_USER_INFO, GET_HKDU_USER_INFO } from '../../../../apollo/graphql/user';
import { GET_E_DRUG_EVENTS_RECORD } from '../../../../apollo/graphql/events';
import { dateFormat, getDuration } from '../../../../utils/dateFormat';
import { getDoctorId, isEventEnrolled, isSkipEventsQuery } from '../../../../utils/doctorUtils';
import { priceByRole } from '../../../../utils/eventPrice';
import { Section } from '../../../Section';
import { PaymentField as Field, RowTitle, RowLink } from '../../../MedicalContent';
import PageNotFound from '../../error/404';

const InfoWrapper = styled(Box).attrs({
  pt: 5,
  pb: 4,
  width: ['100%', '404px'],
})`
  background-color: white;
`;

const Title = styled(Box)`
  font-size: 18px;
  font-weight: 600;
  line-height: 1.33;
  color: black;
`;

const SubTitle = styled(Box).attrs({
  pb: 5,
})`
  font-size: 14px;
  color: #999;
`;

const Divider = styled.div`
  width: 100%;
  min-width: 100%;
  height: 1px;
  background-color: rgba(0, 0, 0, 0.1);
`;

const Price = styled(RowTitle)`
  color: ${themeGet('colors.primary')};
`;

const Checkout = ({ id, eventData, HKDUUserInfo, userInfo }) => {
  if (!R.prop('loading', eventData) && !R.prop('record', eventData)) {
    return <PageNotFound />;
  }

  const record = R.propOr({}, 'record', eventData);
  const title = R.propOr('', 'name', record);
  const doctorId = getDoctorId(HKDUUserInfo);
  const enrolled = isEventEnrolled(record);
  const role = R.pathOr('', ['user', 'role'], userInfo);

  if (enrolled) {
    navigate(`/e-pharm/events/${id}`);
  }

  return (
    <Section>
      <Box py={[4, 8]}>
        <Flex flexDirection={['column', 'row']} alignItems="flex-start">
          <CheckoutForm enrolled={enrolled} doctorId={doctorId} eventId={id} eventType="e_drug_events" />
          <InfoWrapper>
            <Box mx={6}>
              <SubTitle>Your Enrollment</SubTitle>
              <Title>{title}</Title>
              <Box pt={6}>
                <Field label="Date" value={record.date} render={dateFormat} />
                <Field
                  label="Time"
                  value={getDuration(record)}
                  render={value => `${value.startTime}-${value.endTime}`}
                />
                <Field label="Location" value={record.location} />
                <Field
                  label="Event Organizer"
                  value={record.organizer}
                  render={value => (
                    <RowLink as={Link} to={`/e-pharm/event-organizers/${value.id}`}>
                      {value.name}
                    </RowLink>
                  )}
                />
              </Box>
            </Box>
            <Divider />
            <Box mx={6} pt={6}>
              <Price>HK ${priceByRole(record)(role)}</Price>
            </Box>
          </InfoWrapper>
        </Flex>
      </Box>
    </Section>
  );
};

export default R.compose(
  graphql(GET_USER_INFO, { name: 'userInfo' }),
  graphql(GET_HKDU_USER_INFO, {
    name: 'HKDUUserInfo',
    options: ({ userInfo }) => ({
      variables: {
        id: R.pathOr(null, ['user', 'id'], userInfo),
      },
    }),
  }),
  graphql(GET_E_DRUG_EVENTS_RECORD, {
    name: 'eventData',
    skip: isSkipEventsQuery,
    options: ({ id, HKDUUserInfo }) => ({
      variables: {
        id,
        doctor_id: getDoctorId(HKDUUserInfo),
      },
    }),
  })
)(Checkout);
